import { Component, input, output, signal, computed, effect, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { IconComponent } from '../../../icons/icon.component';
import type { NavIconName } from '../../../icons/nav-icon.model';

/**
 * Command Item
 */
export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon?: NavIconName;
  keywords?: string[];
  action?: () => void;
  route?: string;
  shortcut?: string;
  category?: string;
  disabled?: boolean;
}

/**
 * Command Palette Component
 * 
 * Global search and quick actions component activated by Ctrl+K (Cmd+K on Mac).
 * Provides fast keyboard-driven navigation and command execution.
 * 
 * @example
 * ```typescript
 * // In your component
 * commands: CommandItem[] = [
 *   {
 *     id: 'new-program',
 *     label: 'Novo Programa',
 *     description: 'Criar um novo programa',
 *     icon: '➕',
 *     keywords: ['criar', 'adicionar', 'programa'],
 *     route: '/programas/novo',
 *     category: 'Ações',
 *     shortcut: 'Ctrl+N'
 *   },
 *   {
 *     id: 'search-projects',
 *     label: 'Buscar Projetos',
 *     icon: '🔍',
 *     keywords: ['procurar', 'encontrar', 'projeto'],
 *     action: () => this.openProjectSearch(),
 *     category: 'Navegação'
 *   }
 * ];
 * ```
 * 
 * ```html
 * <mg3p-command-palette
 *   [commands]="commands"
 *   [open]="isOpen"
 *   (openChange)="isOpen = $event"
 *   (commandExecute)="onCommandExecute($event)"
 * />
 * ```
 * 
 * Features:
 * - Keyboard activation (Ctrl+K / Cmd+K)
 * - Fuzzy search
 * - Keyboard navigation (Arrow keys, Enter, Esc)
 * - Command categories
 * - Keyboard shortcuts display
 * - Recent commands
 * - WCAG 2.1 Level AA compliant
 */
@Component({
  selector: 'mg3p-command-palette',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    @if (open()) {
      <div
        class="command-palette"
        role="dialog"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-modal]="true"
      >
        <!-- Overlay -->
        <div
          class="command-palette__overlay"
          (click)="close()"
          aria-hidden="true"
        ></div>

        <!-- Dialog -->
        <div class="command-palette__dialog">
          <!-- Search input -->
          <div class="command-palette__search">
            <span class="command-palette__search-icon" aria-hidden="true">
              <mg3p-icon name="search" size="md" />
            </span>
            <input
              #searchInput
              type="text"
              class="command-palette__input"
              [value]="searchQuery()"
              (input)="onSearchChange($event)"
              [placeholder]="placeholder()"
              [attr.aria-label]="'Buscar comandos'"
              [attr.aria-controls]="'command-list'"
              [attr.aria-activedescendant]="selectedIndex() >= 0 ? 'command-' + filteredCommands()[selectedIndex()]?.id : null"
              autocomplete="off"
              spellcheck="false"
            />
            @if (searchQuery()) {
              <button
                type="button"
                class="command-palette__clear"
                (click)="clearSearch()"
                [attr.aria-label]="'Limpar busca'"
              >
                <mg3p-icon name="close" size="sm" aria-hidden="true" />
              </button>
            }
          </div>

          <!-- Results -->
          <div class="command-palette__results">
            @if (filteredCommands().length > 0) {
              <div
                id="command-list"
                class="command-palette__list"
                role="listbox"
                [attr.aria-label]="'Comandos disponíveis'"
              >
                @for (command of groupedCommands(); track command.category) {
                  <div class="command-palette__group">
                    @if (command.category) {
                      <div class="command-palette__category">{{ command.category }}</div>
                    }
                    
                    @for (item of command.items; track item.id; let idx = $index) {
                      <button
                        type="button"
                        class="command-palette__item"
                        [class.command-palette__item--selected]="isSelected(item)"
                        [class.command-palette__item--disabled]="item.disabled"
                        [id]="'command-' + item.id"
                        (click)="executeCommand(item)"
                        (mouseenter)="selectCommand(item)"
                        [disabled]="item.disabled"
                        role="option"
                        [attr.aria-selected]="isSelected(item)"
                        [attr.aria-disabled]="item.disabled"
                      >
                        <div class="command-palette__item-content">
                          @if (item.icon) {
                            <span class="command-palette__icon" aria-hidden="true">
                              <mg3p-icon [name]="item.icon" size="md" />
                            </span>
                          }
                          <div class="command-palette__text">
                            <div class="command-palette__label">{{ item.label }}</div>
                            @if (item.description) {
                              <div class="command-palette__description">{{ item.description }}</div>
                            }
                          </div>
                        </div>
                        @if (item.shortcut) {
                          <kbd class="command-palette__shortcut">{{ item.shortcut }}</kbd>
                        }
                      </button>
                    }
                  </div>
                }
              </div>
            } @else {
              <div class="command-palette__empty">
                <span class="command-palette__empty-icon" aria-hidden="true">
                  <mg3p-icon name="search" size="lg" />
                </span>
                <div class="command-palette__empty-text">
                  {{ searchQuery() ? 'Nenhum comando encontrado' : 'Digite para buscar comandos' }}
                </div>
              </div>
            }
          </div>

          <!-- Footer -->
          <div class="command-palette__footer">
            <div class="command-palette__hint">
              <kbd>↑↓</kbd> Navegar
              <kbd>↵</kbd> Selecionar
              <kbd>Esc</kbd> Fechar
            </div>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .command-palette {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: var(--z-modal);
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 15vh;
    }

    .command-palette__overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      animation: fadeIn var(--transition-normal);
    }

    .command-palette__dialog {
      position: relative;
      width: 100%;
      max-width: 640px;
      max-height: 70vh;
      background: var(--color-surface);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-xl);
      display: flex;
      flex-direction: column;
      animation: slideDown var(--transition-normal);
    }

    /* Search */
    .command-palette__search {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      padding: var(--spacing-md);
      border-bottom: 1px solid var(--color-border);
    }

    .command-palette__search-icon {
      display: flex;
      align-items: center;
      color: var(--color-text-tertiary);
      flex-shrink: 0;
    }

    .command-palette__input {
      flex: 1;
      border: none;
      background: transparent;
      font-size: var(--font-size-lg);
      color: var(--color-text-primary);
      outline: none;
    }

    .command-palette__input::placeholder {
      color: var(--color-text-tertiary);
    }

    .command-palette__clear {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: var(--radius-sm);
      color: var(--color-text-tertiary);
      cursor: pointer;
      transition: all var(--transition-fast);
    }

    .command-palette__clear:hover {
      background: var(--color-surface-hover);
      color: var(--color-text-primary);
    }

    .command-palette__clear:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    /* Results */
    .command-palette__results {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .command-palette__list {
      padding: var(--spacing-sm);
    }

    .command-palette__group {
      margin-bottom: var(--spacing-md);
    }

    .command-palette__group:last-child {
      margin-bottom: 0;
    }

    .command-palette__category {
      padding: var(--spacing-xs) var(--spacing-sm);
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .command-palette__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: var(--spacing-sm) var(--spacing-md);
      background: transparent;
      border: none;
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: all var(--transition-fast);
      text-align: left;
    }

    .command-palette__item:hover:not(.command-palette__item--disabled) {
      background: var(--color-surface-hover);
    }

    .command-palette__item--selected {
      background: var(--color-primary-light);
    }

    .command-palette__item--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .command-palette__item:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: -2px;
    }

    .command-palette__item-content {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      flex: 1;
      min-width: 0;
    }

    .command-palette__icon {
      display: flex;
      align-items: center;
      color: var(--color-text-secondary);
      flex-shrink: 0;
    }

    .command-palette__text {
      flex: 1;
      min-width: 0;
    }

    .command-palette__label {
      font-size: var(--font-size-md);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .command-palette__description {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .command-palette__shortcut {
      display: inline-flex;
      align-items: center;
      padding: var(--spacing-xs) var(--spacing-sm);
      background: var(--color-surface-hover);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      font-size: var(--font-size-xs);
      font-family: var(--font-mono, monospace);
      color: var(--color-text-secondary);
      flex-shrink: 0;
    }

    /* Empty state */
    .command-palette__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-xl);
      text-align: center;
    }

    .command-palette__empty-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-text-tertiary);
      opacity: 0.45;
      margin-bottom: var(--spacing-md);
    }

    .command-palette__empty-text {
      font-size: var(--font-size-md);
      color: var(--color-text-secondary);
    }

    /* Footer */
    .command-palette__footer {
      padding: var(--spacing-sm) var(--spacing-md);
      border-top: 1px solid var(--color-border);
      background: var(--color-surface-secondary);
    }

    .command-palette__hint {
      display: flex;
      align-items: center;
      gap: var(--spacing-md);
      font-size: var(--font-size-xs);
      color: var(--color-text-tertiary);
    }

    .command-palette__hint kbd {
      display: inline-flex;
      align-items: center;
      padding: 2px var(--spacing-xs);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      font-size: var(--font-size-xs);
      font-family: var(--font-mono, monospace);
      margin-right: var(--spacing-xs);
    }

    /* Animations */
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      .command-palette {
        padding-top: 10vh;
      }

      .command-palette__dialog {
        max-width: calc(100% - var(--spacing-lg) * 2);
        max-height: 80vh;
      }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .command-palette__overlay,
      .command-palette__dialog,
      .command-palette__item,
      .command-palette__clear {
        animation: none;
        transition: none;
      }
    }
  `]
})
export class CommandPaletteComponent {
  // Inputs
  commands = input.required<CommandItem[]>();
  open = input<boolean>(false);
  placeholder = input<string>('Digite um comando ou busque...');
  ariaLabel = input<string>('Paleta de comandos');

  // Outputs
  openChange = output<boolean>();
  commandExecute = output<CommandItem>();

  // State
  searchQuery = signal<string>('');
  selectedIndex = signal<number>(0);

  // Computed
  filteredCommands = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    
    if (!query) {
      return this.commands();
    }

    return this.commands().filter(cmd => {
      const labelMatch = cmd.label.toLowerCase().includes(query);
      const descMatch = cmd.description?.toLowerCase().includes(query);
      const keywordsMatch = cmd.keywords?.some(k => k.toLowerCase().includes(query));
      
      return labelMatch || descMatch || keywordsMatch;
    });
  });

  groupedCommands = computed(() => {
    const commands = this.filteredCommands();
    const groups = new Map<string, CommandItem[]>();

    commands.forEach(cmd => {
      const category = cmd.category || 'Outros';
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(cmd);
    });

    return Array.from(groups.entries()).map(([category, items]) => ({
      category,
      items
    }));
  });

  constructor(private router: Router) {
    // Auto-focus search input when opened
    effect(() => {
      if (this.open()) {
        setTimeout(() => {
          const input = document.querySelector('.command-palette__input') as HTMLInputElement;
          input?.focus();
        }, 100);
      }
    });

    // Reset state when closed
    effect(() => {
      if (!this.open()) {
        this.searchQuery.set('');
        this.selectedIndex.set(0);
      }
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboard(event: KeyboardEvent): void {
    // Open with Ctrl+K or Cmd+K
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      this.toggle();
      return;
    }

    if (!this.open()) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.selectNext();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectPrevious();
        break;

      case 'Enter':
        event.preventDefault();
        const selected = this.filteredCommands()[this.selectedIndex()];
        if (selected) {
          this.executeCommand(selected);
        }
        break;
    }
  }

  toggle(): void {
    this.openChange.emit(!this.open());
  }

  close(): void {
    this.openChange.emit(false);
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
    this.selectedIndex.set(0);
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.selectedIndex.set(0);
  }

  selectNext(): void {
    const max = this.filteredCommands().length - 1;
    this.selectedIndex.update(idx => Math.min(idx + 1, max));
    this.scrollToSelected();
  }

  selectPrevious(): void {
    this.selectedIndex.update(idx => Math.max(idx - 1, 0));
    this.scrollToSelected();
  }

  selectCommand(command: CommandItem): void {
    const index = this.filteredCommands().indexOf(command);
    if (index >= 0) {
      this.selectedIndex.set(index);
    }
  }

  isSelected(command: CommandItem): boolean {
    return this.filteredCommands()[this.selectedIndex()] === command;
  }

  executeCommand(command: CommandItem): void {
    if (command.disabled) return;

    this.commandExecute.emit(command);

    if (command.action) {
      command.action();
    }

    if (command.route) {
      this.router.navigate([command.route]);
    }

    this.close();
  }

  private scrollToSelected(): void {
    setTimeout(() => {
      const selected = document.querySelector('.command-palette__item--selected');
      selected?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }
}

// Made with Bob
